import React from 'react';
import moment from 'moment';

/**
 * ServiceCard - Expandable card showing a single service assignment
 *
 * Props:
 * - service: Service object with job/vehicle context
 * - expanded: Boolean - whether card is expanded
 * - onToggle: Function - called when card header is clicked
 * - onStart: Function - called when Start button clicked
 * - onFinish: Function - called when Finish button clicked
 * - actionInProgress: 'start' | 'finish' | null
 * - onShowMaterials: Function - called when materials icon is clicked
 * - onAddNotes: Function - called when Notes button is clicked
 * - timeTrackingRequired: Boolean - whether time tracking check-in is required
 * - isCheckedIn: Boolean - whether technician is currently checked in
 */
const ServiceCard = ({
  service,
  expanded,
  onToggle,
  onStart,
  onFinish,
  actionInProgress,
  onShowMaterials,
  onAddNotes,
  timeTrackingRequired = false,
  isCheckedIn = true
}) => {
  const {
    // Service info
    service_category,
    custom_description,
    service_state,

    // Job/Request context
    Year,
    Make,
    Model,
    Color,
    VIN,
    current_state,

    // Customer info
    customer_name,
    dealerName,
    customer_phone,
    phone_number,

    // Assignment info
    technician_instructions,

    // Timestamps
    started_at,
    finished_at,

    // Materials
    materials,
    material_brand,
    material_product,
    materials_data,

    // Recommended product (selected at booking)
    recommended_product_id,
    recommended_product_name,
    recommended_product_brand
  } = service;

  // Check if materials have been collected
  const hasMaterials = (materials && materials.length > 0) ||
                       (material_brand && material_product) ||
                       (materials_data && (typeof materials_data === 'string' ? materials_data.length > 2 : Object.keys(materials_data).length > 0));

  // Determine display name (customer or dealer)
  const displayName = customer_name || dealerName || 'Unknown';
  const displayPhone = customer_phone || phone_number || '';

  // Vehicle string
  const vehicle = [Year, Make, Model].filter(Boolean).join(' ') || 'Unknown Vehicle';
  const vehicleColor = Color || '';

  // State badge styling
  const getStateBadge = () => {
    switch (service_state) {
      case 'new':
      case 'booked':
        return <span className="badge badge-warning">Not Started</span>;
      case 'started':
        return <span className="badge badge-primary">In Progress</span>;
      case 'finished':
        return <span className="badge badge-success">Completed</span>;
      default:
        return <span className="badge badge-secondary">{service_state}</span>;
    }
  };

  // Notes button helper
  const getNotesButton = () => {
    if (!onAddNotes || service_state === 'finished') return null;
    return (
      <button
        className="btn btn-outline-info ml-2"
        onClick={(e) => { e.stopPropagation(); onAddNotes(service); }}
        title="Add technician notes"
      >
        <i className="fa fa-sticky-note mr-1"></i>
        Notes
      </button>
    );
  };

  // Check if car has been received (request state must be 'received' or later)
  const isCarReceived = current_state === 'received' || current_state === 'started' || current_state === 'completed';

  // Check if time tracking blocks actions
  const isBlockedByTimeTracking = timeTrackingRequired && !isCheckedIn;

  // Get action button
  const getActionButton = () => {
    const isStarting = actionInProgress === 'start';
    const isFinishing = actionInProgress === 'finish';

    switch (service_state) {
      case 'new':
      case 'booked':
        // If car not received, show disabled button with message
        if (!isCarReceived) {
          return (
            <div>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-secondary"
                  disabled
                  title="Vehicle must be checked in before starting work"
                >
                  <i className="fa fa-car mr-2"></i>
                  Awaiting Vehicle
                </button>
                {getNotesButton()}
              </div>
              <small className="text-muted mt-1 d-block">
                <i className="fa fa-info-circle mr-1"></i>
                Vehicle must be checked in before you can start
              </small>
            </div>
          );
        }
        // Show start button - if time tracking required and not checked in, clicking will trigger enforcement modal
        return (
          <div>
            <div className="d-flex align-items-center">
              <button
                className={`btn ${isBlockedByTimeTracking ? 'btn-outline-success' : 'btn-success'}`}
                onClick={(e) => { e.stopPropagation(); onStart(); }}
                disabled={isStarting}
              >
                {isStarting ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2"></i>
                    Starting...
                  </>
                ) : (
                  <>
                    <i className="fa fa-play mr-2"></i>
                    Start Service
                  </>
                )}
              </button>
              {getNotesButton()}
            </div>
            {/* Show check-in reminder if time tracking is required but not checked in */}
            {isBlockedByTimeTracking && (
              <small className="text-warning mt-1 d-block">
                <i className="fa fa-clock-o mr-1"></i>
                Check in required before starting
              </small>
            )}
          </div>
        );
      case 'started':
        return (
          <div className="d-flex align-items-center">
            <button
              className="btn btn-primary"
              onClick={(e) => { e.stopPropagation(); onFinish(); }}
              disabled={isFinishing}
            >
              {isFinishing ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2"></i>
                  Completing...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-2"></i>
                  Complete Service
                </>
              )}
            </button>
            {getNotesButton()}
          </div>
        );
      case 'finished':
        return (
          <div className="text-center text-success py-2">
            <i className="fa fa-check-circle mr-2"></i>
            Completed {finished_at && moment(finished_at).fromNow()}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`card mb-3 service-card ${expanded ? 'border-primary' : ''}`}>
      {/* Collapsed Header - Always Visible */}
      <div
        className="card-body py-3"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            {/* Service Name - Category followed by description, with materials icon */}
            <h6 className="mb-1 d-flex align-items-center">
              <span>{service_category}{custom_description ? ` - ${custom_description}` : ''}</span>
              {hasMaterials && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onShowMaterials) {
                      onShowMaterials(service);
                    }
                  }}
                  title="View materials used"
                >
                  <i className="fa fa-cubes text-info"></i>
                </button>
              )}
            </h6>

            {/* Recommended Product - Show what material to use (only if no materials collected yet) */}
            {!hasMaterials && recommended_product_id && recommended_product_name && (
              <div className="small text-success mb-1">
                <i className="fa fa-check mr-1"></i>
                <strong>Use:</strong>{' '}
                {recommended_product_brand
                  ? `${recommended_product_brand} - ${recommended_product_name}`
                  : recommended_product_name}
              </div>
            )}

            {/* Vehicle */}
            <div className="text-muted small">
              {vehicleColor && <span className="mr-2">{vehicleColor}</span>}
              {vehicle}
            </div>

            {/* Customer */}
            <div className="text-muted small">
              {displayName}
            </div>
          </div>

          <div className="text-right">
            {/* State Badge */}
            {getStateBadge()}

            {/* Expand Indicator */}
            <div className="mt-2 text-muted">
              <i className={`fa fa-chevron-${expanded ? 'up' : 'down'}`}></i>
            </div>
          </div>
        </div>

        {/* Instructions Preview (if any, show in collapsed state) */}
        {technician_instructions && technician_instructions.trim() && !expanded && (
          <div className="mt-2 pt-2 border-top">
            <small className="text-info">
              <i className="fa fa-sticky-note mr-1"></i>
              <strong>Service Instructions:</strong>{' '}
              {technician_instructions.length > 50
                ? technician_instructions.substring(0, 50) + '...'
                : technician_instructions
              }
            </small>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="card-body pt-0 border-top">
          {/* Vehicle Details */}
          <div className="row small mb-3">
            <div className="col-12">
              <strong>Vehicle:</strong> {vehicleColor} {vehicle}
            </div>
            {VIN && (
              <div className="col-12 mt-1">
                <strong>VIN:</strong> <span className="font-monospace">{VIN}</span>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="row small mb-3">
            <div className="col-12">
              <strong>Customer:</strong> {displayName}
            </div>
            {displayPhone && (
              <div className="col-12 mt-1">
                <strong>Phone:</strong>{' '}
                <a href={`tel:${displayPhone}`}>{displayPhone}</a>
              </div>
            )}
          </div>

          {/* Timestamps */}
          {(started_at || finished_at) && (
            <div className="row small mb-3 text-muted">
              {started_at && (
                <div className="col-6">
                  <i className="fa fa-play mr-1"></i>
                  Started: {moment(started_at).format('h:mm A')}
                </div>
              )}
              {finished_at && (
                <div className="col-6">
                  <i className="fa fa-check mr-1"></i>
                  Finished: {moment(finished_at).format('h:mm A')}
                </div>
              )}
            </div>
          )}

          {/* Recommended Product - prominent display in expanded view */}
          {!hasMaterials && recommended_product_id && recommended_product_name && (
            <div className="mb-3 p-2 bg-light rounded border-left border-success" style={{ borderLeftWidth: '4px' }}>
              <div className="text-success">
                <i className="fa fa-check mr-2"></i>
                <strong>Recommended Product:</strong>
              </div>
              <div className="ml-4">
                {recommended_product_brand
                  ? `${recommended_product_brand} - ${recommended_product_name}`
                  : recommended_product_name}
              </div>
            </div>
          )}

          {/* Technician Instructions - Above action button */}
          {technician_instructions && technician_instructions.trim() && (
            <div className="mb-3">
              <strong>Service Instructions:</strong> <strong>{technician_instructions}</strong>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-3">
            {getActionButton()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCard;
